import { z } from "zod";
import { emailSchema, textSchema, dateSchema, optionalPhoneSchema } from "@/shared/lib/schemas/common.schema";
import { makeOptional } from "@/shared/lib/helpers/schema.helpers";
import { genderOptions } from "@/features/profile/schemas/profile-form.schema";

export const NO_EMAIL_AVAILABLE_OPTION = "No Email Available";
const NO_EMAIL_PLACEHOLDER_REGEX = /^no-email\+.+@reliefhub\.local$/i;

export function isNoEmailPlaceholderValue(value: string | null | undefined): boolean {
    const normalized = (value ?? "").trim();
    if (!normalized) return false;
    if (normalized.toLowerCase() === NO_EMAIL_AVAILABLE_OPTION.toLowerCase()) return true;
    return NO_EMAIL_PLACEHOLDER_REGEX.test(normalized);
}

export function getDisplayEmailValue(value: string | null | undefined): string {
    if (!value) return "";
    return isNoEmailPlaceholderValue(value) ? NO_EMAIL_AVAILABLE_OPTION : value;
}

const optionalGenderSchema = makeOptional(
    z.enum(genderOptions.map((o) => o.value) as [string, ...string[]])
);

export const volunteersFormSchema = z.object({
    fullName: textSchema(),
    email: z.union([emailSchema, z.literal(NO_EMAIL_AVAILABLE_OPTION)]),
    phone: optionalPhoneSchema,
    preferredLocation: makeOptional(z.string().trim().max(300)),
    organizationName: makeOptional(z.string().trim().max(100)),
    dateOfBirth: makeOptional(dateSchema, "date"),
    gender: optionalGenderSchema,
    role: z.number(),
    servePeriods: z.array(z.object({
        from: z.any().transform((val) => {
            if (!val || val === undefined || val === null || val === '') return undefined;
            if (val instanceof Date) return val;
            if (typeof val === 'string') {
                const parsed = new Date(val);
                return isNaN(parsed.getTime()) ? undefined : parsed;
            }
            return undefined;
        }).pipe(z.union([z.date(), z.undefined()])),
        to: z.any().transform((val) => {
            if (!val || val === undefined || val === null || val === '') return undefined;
            if (val instanceof Date) return val;
            if (typeof val === 'string') {
                const parsed = new Date(val);
                return isNaN(parsed.getTime()) ? undefined : parsed;
            }
            return undefined;
        }).pipe(z.union([z.date(), z.undefined()])),
        regionId: z.union([z.number(), z.string()]).transform((val) => {
            if (val == null || val === '') return undefined;
            return typeof val === 'string' ? parseInt(val, 10) : val;
        }).optional(),
    })).optional(),
    profilePicture: makeOptional(z
        .any()
        .refine(
            (file) => {
                if (file && file.type && !file.type.includes("image")) {
                    return false
                }
                return true
            },
            { message: "Image should be an image" }
        )
        .or(z.string())
        .optional(),
    )
})

export const updateVolunteerFormSchema = volunteersFormSchema.partial()

/** Single volunteer entry (no role) for use in create-multiple flow */
const singleVolunteerEntrySchema = volunteersFormSchema.omit({ role: true })

/** Schema for adding multiple volunteers at once */
export const createVolunteersFormSchema = z.object({
    volunteers: z.array(singleVolunteerEntrySchema).min(1, "Add at least one volunteer"),
})

export type VolunteerFormType = z.infer<typeof volunteersFormSchema>
export type UpdateVolunteerFormType = z.infer<typeof updateVolunteerFormSchema>
export type SingleVolunteerEntryType = z.infer<typeof singleVolunteerEntrySchema>
export type CreateVolunteersFormType = z.infer<typeof createVolunteersFormSchema>